import classNames from "classnames";
import { Link, useMatch } from "react-router";

import { RobotoDivider } from "@/shared/components/RobotoDivider";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Badge } from "@/shared/foundation/primitives";

import styles from "./Navigation.module.css";
import { navGroups, NavLink } from "./navigationLinks";

interface NavigationProps {
  /** Callback to be invoked when a navigation link is clicked. */
  onNavigate?: () => void;
}

/**
 * Sidebar navigation: feature links, actions and agents, boards when
 * `release-boards` is on for the current org, settings/guides, and an admin
 * block for system users.
 */
export function Navigation(props: NavigationProps) {
  return (
    <div>
      <NavList links={navGroups.features} onNavigate={props.onNavigate} />

      <RobotoDivider />

      <NavList
        links={navGroups.actionsAndAgents}
        onNavigate={props.onNavigate}
      />

      <BoardsNavGroup onNavigate={props.onNavigate} />

      <RobotoDivider />

      <NavList
        links={navGroups.settingsAndGuides}
        onNavigate={props.onNavigate}
      />

      <SystemUserOnly>
        <RobotoDivider />
        <NavList links={navGroups.admin} onNavigate={props.onNavigate} />
      </SystemUserOnly>
    </div>
  );
}

interface NavListProps {
  links: NavLink[];
  onNavigate?: () => void;
}

function NavList(props: NavListProps) {
  return (
    <ul className={styles.navList}>
      {props.links.map((link) => (
        <NavListItem
          key={link.name}
          link={link}
          onNavigate={props.onNavigate}
        />
      ))}
    </ul>
  );
}

/**
 * Renders the Boards group and the divider above it only when `release-boards`
 * is on.
 */
function BoardsNavGroup(props: NavigationProps) {
  const boardsEnabled = useCheckFeatureFlag("release-boards");

  if (!boardsEnabled) {
    return null;
  }

  return (
    <>
      <RobotoDivider />
      <NavList links={navGroups.boards} onNavigate={props.onNavigate} />
    </>
  );
}

interface NavListItemProps {
  link: NavLink;
  onNavigate?: () => void;
}

function NavListItem(props: NavListItemProps) {
  return (
    <li className={styles.navItem}>
      <NavItem {...props.link} onClick={props.onNavigate} />
    </li>
  );
}

/**
 * A drawer navigation link button
 */
function NavItem(props: NavLink & { onClick?: () => void }) {
  const match = useMatch(props.link);
  const isActive = match !== null;

  const isExternal = props.isExternalLink;

  return (
    <Link
      to={props.link}
      target={isExternal ? "_blank" : "_self"}
      rel={isExternal ? "noopener noreferrer" : undefined}
      aria-label={isExternal ? `${props.name} (opens in new tab)` : props.name}
      className={classNames(styles.navLink, {
        [styles.active]: isActive,
      })}
      onClick={props.onClick}
    >
      <props.icon className={styles.navIcon} aria-hidden="true" />
      <RobotoTypography variant="body2" className={styles.linkText}>
        <span className={styles.linkName}>{props.name}</span>
        {props.badge === undefined ? null : (
          <Badge variant="brand">{props.badge}</Badge>
        )}
      </RobotoTypography>
    </Link>
  );
}
